import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import Container from "@material-ui/core/Container";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "../auth/authActions";

import { clone, find, map, mergeRight, propEq } from "ramda";
import { isBlank, notBlank } from "../util/ext";
import PanelLoadingIndicator from "../components/PanelLoadingIndicator";
import AddIcon from "@material-ui/icons/Add";
import TotalsHeader from "../creator-application/common/TotalsHeader";
import { useSearchFilter } from "../common/use-searchFilter";
import { Box } from "@material-ui/core";
import { useSnackAlert } from "../common/use-snackAlert";
import RealTimeSearchBar from "../creator-application/common/RealTimeSearchBar";
import { fetchSecurityUser } from "../util/fetchUtils";
import OrganizationTable from "./OrganizationTable";
import ListButton from "./ListButton";
import NewOrganizationDialog from "./NewOrganizationDialog";
import { useHistory, useLocation } from "react-router-dom";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";

export const OrganizationTypeCodes = {
  library: "LIBRARY",
  buying_group: "BUYING_GROUP",
  publisher: "PUBLISHER",
  default: "DEFAULT",
};

const organizationTypes = [
  { value: OrganizationTypeCodes.library, display: "Library" },
  { value: OrganizationTypeCodes.buying_group, display: "Buying Group" },
  { value: OrganizationTypeCodes.publisher, display: "Publisher" },
  { value: OrganizationTypeCodes.default, display: "Default" },
];

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    root: {
      border: "3px white solid",
      backgroundColor: "white",
      borderRadius: "20px",
    },

    panel: {
      backgroundColor: "white",
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
      minHeight: "calc(100vh - 500px)",
    },

    listContainer: {
      height: "calc(100vh - 350px)",
      overflow: "auto",
    },
  }));

const userCache = new Map();
const lookupUser = (auth, userId) => {
  if (!userCache.has(userId)) {
    userCache.set(userId, fetchSecurityUser(auth)(userId));
  }

  return userCache.get(userId);
};

const OrganizationsBrowse = ({
  searchState,
  onSearchStateChange,
  searchResults,
}) => {
  const [scrollVisible, setScrollVisible] = useState(true);
  const auth = useSelector(state => state.auth);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const classes = useStyles(scrollVisible)();
  const { SnackAlert, _showAlert } = useSnackAlert();

  const [organizations, setOrganizations] = useState([]);

  // //-- Handle Search state
  const initialState = clone(searchState);
  const {
    state,
    handleUpdateSort,
    handleUpdatePage,
    handleUpdateFilter,
    handleUpdateActiveOnly,
  } = useSearchFilter(initialState);

  //-- Whenever the internal search state changes, update the search state stored in the parent
  useEffect(() => {
    console.log(state);
    onSearchStateChange(state);
    setScrollVisible(true);
  }, [onSearchStateChange, state]);

  useEffect(() => {
    if (searchResults.isError && searchResults.error.status === 401) {
      dispatch(logOut());
    }

    const fetchUsers = async org => {
      const createdByUser = await lookupUser(auth, org.createdBy);
      const updatedByUser = await lookupUser(auth, org.updatedBy);

      return mergeRight(org, {
        createdByUsername: createdByUser.username,
        updatedByUsername: updatedByUser.username,
      });
    };

    if (searchResults.isSuccess) {
      setScrollVisible(true);
      console.log("Calling lookup user...");
      Promise.all(map(fetchUsers, searchResults.data))
        .then(data => {
          console.log("Got the users...");
          setOrganizations(data);
        })
        .catch(err => {
          console.log(err);
        });
    }
  }, [
    dispatch,
    auth,
    searchResults.data,
    searchResults.isError,
    searchResults.error,
    searchResults.isSuccess,
  ]);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newOrganizationType, setNewOrganizationType] = useState(null);
  const handleNewOrganizationClicked = useCallback(value => {
    console.log("Handling Edit click...");
    const type = find(propEq("value", value), organizationTypes);
    setNewOrganizationType(type);
    setDialogOpen(true);
  }, []);

  const handleNewCancel = useCallback(() => {
    setDialogOpen(false);
    setNewOrganizationType(null);
  }, []);

  const handleNewSave = useCallback(
    async data => {
      await searchResults.refetch();
      setDialogOpen(false);
      setNewOrganizationType(null);
      history.push(
        `/manage-organizations/organizations/${data.organizationId}`,
        {
          previousLocation: location.pathname,
        },
      );
    },
    [searchResults, history, location.pathname],
  );

  return (
    <Container id="organization-browse-view" classes={{ root: classes.root }}>
      <SnackAlert />

      <div>
        <RealTimeSearchBar
          query={state.appliedFilter}
          onQueryChange={handleUpdateFilter}
          otherFilters={
            <FormControlLabel
              control={
                <Switch
                  checked={state.activeOnly}
                  onChange={event =>
                    handleUpdateActiveOnly(event.target.checked)
                  }
                  name="active-only"
                  color="primary"
                />
              }
              label="Show Active Only"
            />
          }
        />
        <TotalsHeader
          countText="Organization"
          count={searchResults.isSuccess ? organizations.length : 0}
          showCount={false}
          selectedActions={
            <Box>
              <ListButton
                title="New Organization"
                icon={<AddIcon />}
                variant="button"
                options={organizationTypes}
                onSelect={handleNewOrganizationClicked}
              />
            </Box>
          }
        />
        <Container classes={{ root: classes.panel }}>
          {searchResults.isError && (
            <Box padding={5} color="error" fontSize="1.4rem" fontStyle="italic">
              Error loading Associated Organizations.
            </Box>
          )}
          {searchResults.isLoading && <PanelLoadingIndicator />}
          {searchResults.isSuccess && isBlank(organizations) && (
            <Box padding={5} fontSize="1.4rem" fontStyle="italic">
              No Organizations found matching query.
            </Box>
          )}
          {searchResults.isSuccess && notBlank(organizations) && (
            <OrganizationTable
              organizationList={organizations}
              page={state.page}
              orderBy={state.orderBy}
              order={state.orderDirection}
              onSortChange={handleUpdateSort}
              onPageChange={handleUpdatePage}
              onScrollVisible={handleScrollVisible}
            />
          )}
        </Container>
      </div>

      {dialogOpen && (
        <NewOrganizationDialog
          open={dialogOpen}
          type={newOrganizationType}
          onCancel={handleNewCancel}
          onSave={handleNewSave}
        />
      )}
    </Container>
  );
};

export default OrganizationsBrowse;
